import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { assetUrl, loadConfig } from "@/lib/brand";
import { LANGS, LANG_NAMES, pickLang } from "@/i18n/booking";
import LegalFooter from "@/components/public/LegalFooter";

// Marco de las páginas públicas. Incrustada en un iframe, avisa al padre de su
// altura (widget.js) y no pinta cabecera.
export default function PublicLayout({ lang, embedded = false, children }) {
  const [config, setConfig] = useState(null);
  const ref = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    loadConfig().then((data) => {
      if (!data) return;
      setConfig(data);
      // Ya con el idioma por defecto de la empresa: si cambia la elección, se aplica.
      const wanted = pickLang(location.search);
      if (wanted !== lang) {
        const p = new URLSearchParams(location.search);
        p.set("lang", wanted);
        navigate(`${location.pathname}?${p.toString()}`, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!embedded || !ref.current || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      window.parent?.postMessage({ type: "avantrent:height", height: ref.current.scrollHeight + 16 }, "*");
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [embedded]);

  const withLang = (l) => {
    const p = new URLSearchParams(location.search);
    p.set("lang", l);
    return `${location.pathname}?${p.toString()}`;
  };

  return (
    <div ref={ref} className={embedded ? "bg-white" : "min-h-screen bg-[#F7F5F0]"}>
      {!embedded && (
        <header className="bg-white border-b border-[#DCD8D0]">
          <div className="container-x py-4 flex items-center justify-between gap-4">
            <Link to={`/reservar?lang=${lang}`} className="flex items-center gap-3 min-w-0 font-display text-xl font-bold text-[#1A1A1A]" data-testid="public-brand">
              {config?.brand?.logo_url && <img src={assetUrl(config.brand.logo_url)} alt="" className="h-10 w-auto max-w-[45vw] object-contain" data-testid="public-logo" />}
              <span className="truncate">{config?.company_name || ""}</span>
            </Link>
            {/* En el móvil los idiomas van con su código (ES, CA...): con el nombre entero no cabían. */}
            <nav className="flex shrink-0 gap-2 text-sm" data-testid="public-langs">
              {LANGS.map((l) => (
                <Link key={l} to={withLang(l)} lang={l} title={LANG_NAMES[l]} aria-label={LANG_NAMES[l]} className={l === lang ? "font-semibold text-marca" : "text-[#5C5C5C] hover:text-marca"}>
                  <span className="sm:hidden">{l.toUpperCase()}</span>
                  <span className="hidden sm:inline">{LANG_NAMES[l]}</span>
                </Link>
              ))}
            </nav>
          </div>
        </header>
      )}
      <main className={embedded ? "p-4" : "container-x py-8"}>{children({ config })}</main>
      <LegalFooter lang={lang} brand={config?.brand} embedded={embedded} />
    </div>
  );
}
